/**
 * Shared bounded fetch for medication logs.
 *
 * With a full date range, requests are chunked into server-accepted windows
 * (padded and split by `getMedicationLogServerWindows`) and each window is
 * drained through the keyset cursor, so a busy range is never silently
 * truncated at one page. Without a range, a single capped page is requested —
 * the pre-#664 compatibility behavior.
 */

import type { useApiClient } from "../api/louise-api";
import type { paths } from "../openapi/schema";
import {
  getMedicationLogServerWindows,
  MEDICATION_LOG_COMPATIBILITY_FETCH_LIMIT,
} from "./medication-log-date";

type LouiseApiClient = ReturnType<typeof useApiClient>["client"];

export type MedicationLogRow =
  paths["/patients/{patientId}/medications/logs"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

/**
 * Upper bound on cursor pages fetched per window (2000 rows at the server
 * page size) so a pathological history cannot loop indefinitely.
 */
export const MEDICATION_LOG_MAX_PAGES_PER_WINDOW = 10;

export type MedicationLogFetchResult =
  | { data: MedicationLogRow[]; error?: never }
  | { data?: never; error: unknown; status: number };

function occurrenceSortKey(log: MedicationLogRow): string {
  return log.scheduledFor ?? log.takenAt;
}

export async function fetchMedicationLogsInRange(
  client: LouiseApiClient,
  patientId: string,
  options: {
    startDate?: string;
    endDate?: string;
    timezone?: string;
  },
): Promise<MedicationLogFetchResult> {
  const windows = getMedicationLogServerWindows(
    options.startDate,
    options.endDate,
  );

  if (!windows) {
    const response = await client.GET(
      "/patients/{patientId}/medications/logs",
      {
        params: {
          path: { patientId },
          query: { limit: MEDICATION_LOG_COMPATIBILITY_FETCH_LIMIT },
        },
      },
    );
    if (response.error) {
      return { error: response.error, status: response.response.status };
    }
    return { data: response.data.data };
  }

  const rowsById = new Map<string, MedicationLogRow>();
  for (const window of windows) {
    let cursor: string | undefined;
    for (let page = 0; page < MEDICATION_LOG_MAX_PAGES_PER_WINDOW; page++) {
      const response = await client.GET(
        "/patients/{patientId}/medications/logs",
        {
          params: {
            path: { patientId },
            query: {
              startDate: window.startDate,
              endDate: window.endDate,
              timezone: options.timezone,
              limit: MEDICATION_LOG_COMPATIBILITY_FETCH_LIMIT,
              cursor,
            },
          },
        },
      );
      if (response.error) {
        return { error: response.error, status: response.response.status };
      }

      const rows = response.data.data;
      for (const row of rows) {
        rowsById.set(row.id, row);
      }
      if (rows.length < MEDICATION_LOG_COMPATIBILITY_FETCH_LIMIT) break;
      cursor = rows[rows.length - 1]?.id;
      if (!cursor) break;
    }
  }

  // Windows return newest-first individually; restore one global occurrence
  // ordering to match single-request server semantics.
  const merged = [...rowsById.values()].sort((a, b) => {
    const byOccurrence = occurrenceSortKey(b).localeCompare(
      occurrenceSortKey(a),
    );
    return byOccurrence !== 0 ? byOccurrence : b.id.localeCompare(a.id);
  });
  return { data: merged };
}
